import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '../store';
import { sortedBrandsByDailySeriesVisibility, sortedBrandsByWeeklyScores } from '../utils/sortedBrands';
import { scoreWeeklyFromDailyBrandSeries } from '../scores/utils/scoreWeeklyFromDailyBrandSeries';

import type { Brand } from '../../types/brand';



export const selectBrandLoading = (state: RootState) => state.brands.loading;
export const selectBrandLoadError = (state: RootState) => state.brands.loadError;
export const selectBrandState = (state: RootState) => state.brands.brand;
export const selectSelectedBrandIdState = (state: RootState) => state.brands.selectedBrandId;
export const selectViewBrandIdState = (state: RootState) => state.brands.viewBrandId;
export const selectListBrandsState = (state: RootState) => state.brands.listBrands;

export const selectBrandWithCompetitorsState = createSelector(
  (state: RootState) => state.brands.brand,
  (state: RootState) => state.brands.competitors,
  (state: RootState) => state.brands.listBrands,
  selectSelectedBrandIdState,
  (brand, competitors, listBrands, selectedBrandId): Brand[] => {
    const comp = competitors ?? [];
    if (selectedBrandId == null) {
      return [brand ?? null, ...comp].filter((b): b is Brand => b != null);
    }
    const resolvedMain =
      listBrands?.find((b) => !b.competitor && b.isActive && b.id === selectedBrandId) ??
      (brand != null && brand.id === selectedBrandId ? brand : null);
    if (!resolvedMain) {
      return [brand ?? null, ...comp].filter((b): b is Brand => b != null);
    }
    const scoped = comp.filter((c) => c.mainBrandId === resolvedMain.id);
    return [resolvedMain, ...scoped];
  },
);

export const selectDailyPageSelectedCompetitorBrandId = (state: RootState) => state.dailyPage.selectedCompetitorBrandId;

export const selectActiveCompetitorsState = createSelector(
  (state: RootState) => state.brands.competitors,
  selectSelectedBrandIdState,
  (competitors, selectedBrandId) =>
    competitors?.filter(
      (c) =>
        c.isActive &&
        (selectedBrandId == null || c.mainBrandId === selectedBrandId),
    ) ?? null,
);

/** Selected brand id first, then active competitor ids (same order as score fetch queries). */
export const selectSelectedBrandAndActiveCompetitorIdsState = createSelector(
  selectSelectedBrandIdState,
  selectActiveCompetitorsState,
  (selectedBrandId, competitors): number[] => {
    const competitorIds = (competitors ?? []).map((c) => c.id);
    if (selectedBrandId == null) return competitorIds;
    return [selectedBrandId, ...competitorIds];
  },
);

export const selectInactiveCompetitorsState = createSelector(
  (state: RootState) => state.brands.competitors,
  selectSelectedBrandIdState,
  (competitors, selectedBrandId) =>
    competitors?.filter(
      (c) =>
        !c.isActive &&
        (selectedBrandId == null || c.mainBrandId === selectedBrandId),
    ) ?? null,
);

export const selectAddNewBrandBrandState = (state: RootState) => state.brands.addNewBrand.brand;
export const selectAddNewBrandCompetitorsState = (state: RootState) => state.brands.addNewBrand.competitors;
export const selectAddNewBrandSelectedCompetitorsState = (state: RootState) => state.brands.addNewBrand.selectedCompetitors;
export const selectAddNewBrandLoadingState = (state: RootState) => state.brands.addNewBrand.loading;

export const selectActiveCompetitorsCount = createSelector(
  selectActiveCompetitorsState,
  (competitors): number => competitors?.length ?? 0,
);

export const selectTotalCompetitorsCount = createSelector(
  (state: RootState) => state.brands.competitors,
  (competitors): number => competitors?.length ?? 0,
);

export const selectActiveCompetitorIdsSignatureState = createSelector(
  selectActiveCompetitorsState,
  (activeForMain): string | null => {
    if (activeForMain == null) return null;
    const ids = activeForMain.map((c) => c.id).sort((a, b) => a - b);
    return ids.join(',');
  },
);

/** Active competitors with daily series attached; order by latest daily visibility. */
export const selectActiveCompetitorsSortedByWeeklyScoresState = createSelector(
  (state: RootState) => state.brands.competitors,
  (state: RootState) => state.scores.dailyAverageBrand.data,
  (competitors, dailyAverageBrand): Brand[] | null => {
    if (!competitors || !dailyAverageBrand) return null;

    const activeCompetitors = competitors
      .filter((c) => c.isActive)
      .map((competitor) => ({
        ...competitor,
        scores: dailyAverageBrand[String(competitor.id)] ?? { scores: [] },
      }));

    return sortedBrandsByDailySeriesVisibility(activeCompetitors);
  },
);

export const selectActiveBrandsWithScoresState = createSelector(
  (state: RootState) => state.brands.brand,
  (state: RootState) => state.brands.competitors,
  (state: RootState) => state.scores.dailyAverageBrand.data,
  (state: RootState) => state.scores.lastUpdates.data,
  (brand, competitors, scores, lastUpdates) => {
    if (!brand || !competitors || !scores || !lastUpdates) {
      return null;
    }

    const activeBrands = [brand, ...competitors]
      .filter((b): b is Brand => Boolean(b && b.isActive))
      .map((b) => {
        const item = scores[String(b.id)];
        const weeklyScore = scoreWeeklyFromDailyBrandSeries(b.id, item, b.name);
        return {
          ...b,
          lastUpdateTime: lastUpdates[b.id],
          scores: item,
          weeklyScores: weeklyScore,
        };
      });

    return sortedBrandsByWeeklyScores(activeBrands);
  },
);

export const selectActiveBrandsState = createSelector(
  (state: RootState) => state.brands.brand,
  (state: RootState) => state.brands.competitors,
  (state: RootState) => state.brands.listBrands,
  selectSelectedBrandIdState,
  (brand, competitors, listBrands, selectedBrandId): Brand[] | null => {
    const comp = competitors ?? [];

    const resolvedMain: Brand | null =
      selectedBrandId != null
        ? listBrands?.find((b) => !b.competitor && b.isActive && b.id === selectedBrandId) ??
          (brand != null && brand.id === selectedBrandId ? brand : null)
        : brand ?? null;

    if (!resolvedMain) return null;

    const activeForMain = comp.filter(
      (c) => c.isActive && c.competitor && c.mainBrandId === resolvedMain.id,
    );

    return [resolvedMain, ...activeForMain].filter((b): b is Brand => Boolean(b && b.isActive));
  },
);

export const selectSelectedBrandState = createSelector(
  selectViewBrandIdState,
  selectActiveBrandsState,
  (id, brands): Brand | null => {
    if (id == null || !brands) return null;
    return brands.find((b) => b.id === id) ?? null;
  },
);

export const selectSelectedCompetitorBrandState = createSelector(
  selectDailyPageSelectedCompetitorBrandId,
  selectActiveBrandsState,
  (id, brands): Brand | null => {
    if (id == null || !brands) return null;
    return brands.find((b) => b.id === id) ?? null;
  },
);

export const selectBestCompetitorByWeeklyVisibilityState = createSelector(
  selectActiveBrandsWithScoresState,
  (brands): Brand | null => {
    if (!brands?.length) return null;

    const competitors = brands.filter((b) => b.competitor);
    if (!competitors.length) return null;

    const withWeeklyScores = competitors.filter((c) => c.weeklyScores);
    if (!withWeeklyScores.length) {
      return competitors[0] ?? null;
    }

    return withWeeklyScores.reduce((best, current) => {
      const bestValue =
        best.weeklyScores?.current_week_avg_visibility ?? -Infinity;
      const currentValue =
        current.weeklyScores?.current_week_avg_visibility ?? -Infinity;
      return currentValue > bestValue ? current : best;
    }, withWeeklyScores[0]);
  },
);

